// src/services/financialService.js
import {
    collection, query, orderBy, limit, startAfter, getDocs,
    serverTimestamp, doc, runTransaction, updateDoc, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { getPaths } from '../firebase.paths';
import { globalAppId } from '../utils/constants';

const paths = getPaths(globalAppId);

export const FinancialService = {
    // 🟢 1. LEITURA (Faltava isso)
    getTransactions: async (lastDoc = null, pageSize = 50) => {
        let q = query(
            collection(db, paths.transactions), 
            orderBy('createdAt', 'desc'), 
            limit(pageSize)
        );
        if (lastDoc) q = query(q, startAfter(lastDoc));

        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(d => ({ 
            id: d.id, 
            ...d.data(), 
            createdAt: d.data().createdAt?.toDate() || new Date() 
        }));
        
        return { data, lastDoc: snapshot.docs[snapshot.docs.length - 1] };
    },

    // 🟢 2. KUDOS
    sendKudos: async (senderId, senderName, targetUserId, message) => {
        if (senderId === targetUserId) throw new Error("Auto-kudos não permitido.");

        // ID previsível garante atomicidade: dois requests simultâneos tentarão
        // criar o mesmo documento — o segundo verá que já existe e lançará erro.
        const now = new Date();
        const lockId = `kudos_${senderId}_${now.getFullYear()}_${now.getMonth() + 1}`;
        const lockRef = doc(db, paths.transactions, lockId);

        await runTransaction(db, async (transaction) => {
            const lockSnap = await transaction.get(lockRef);
            if (lockSnap.exists()) {
                throw new Error("Você já enviou seu Valeu! deste mês.");
            }
            transaction.set(lockRef, {
                targetUserId,
                targetUserName: 'Colega', // Atualizado ao aprovar
                adminId: senderId,
                adminName: senderName,
                pointsChange: 50,
                creditsChange: 50,
                reason: `Solicitação de Valeu!: ${message}`,
                type: 'kudos_request',
                status: 'pending',
                createdAt: serverTimestamp()
            });
        });
    },

    approveKudos: async (transactionId, approverName) => {
        await runTransaction(db, async (transaction) => {
            const transRef = doc(db, paths.transactions, transactionId);
            const transSnap = await transaction.get(transRef);
            if (!transSnap.exists()) throw new Error("Transação não encontrada.");
            
            const data = transSnap.data();
            if (data.status === 'approved') throw new Error("Já aprovado.");

            const targetRef = doc(db, paths.users, data.targetUserId);
            const userSnap = await transaction.get(targetRef);
            const targetName = userSnap.exists() ? userSnap.data().name : 'Usuário';

            // Se usuário existe, dá os pontos
            if (userSnap.exists()) {
                const currentPoints = userSnap.data().points || 0;
                const currentCredits = userSnap.data().credits || 0;
                
                transaction.update(targetRef, { 
                    points: currentPoints + (data.pointsChange || 0), 
                    credits: currentCredits + (data.creditsChange || 0) 
                });
            }

            // Atualiza status da transação
            transaction.update(transRef, { 
                status: 'approved', 
                targetUserName: targetName, 
                approvedAt: serverTimestamp(), 
                approvedBy: approverName, 
                reason: data.reason.replace('Solicitação de ', '') 
            });
        });
    },

    // 🟢 3. GESTÃO DE PONTOS
    updatePoints: async (targetUserId, amount, reason, adminName) => {
        const points = Number(amount);
        await runTransaction(db, async (transaction) => {
            const userRef = doc(db, paths.users, targetUserId);
            const userSnap = await transaction.get(userRef);
            if (!userSnap.exists()) throw new Error("Usuário não encontrado.");

            const currentPoints = userSnap.data().points || 0;
            const currentCredits = userSnap.data().credits || 0;

            transaction.update(userRef, { 
                points: currentPoints + points, 
                credits: currentCredits + points 
            });

            const transRef = doc(collection(db, paths.transactions));
            transaction.set(transRef, {
                targetUserId,
                targetUserName: userSnap.data().name,
                adminName: adminName,
                pointsChange: points,
                creditsChange: points,
                reason,
                type: 'manual_adjustment',
                status: 'approved',
                createdAt: serverTimestamp()
            });
        });
    },

    resetPoints: async (targetUserId, reason, adminName) => {
        await runTransaction(db, async (transaction) => {
            const userRef = doc(db, paths.users, targetUserId);
            const userSnap = await transaction.get(userRef);
            if (!userSnap.exists()) throw new Error("Usuário não encontrado.");

            const currentPoints = userSnap.data().points || 0;
            const currentCredits = userSnap.data().credits || 0;

            transaction.update(userRef, { points: 0, credits: 0 });

            const transRef = doc(collection(db, paths.transactions));
            transaction.set(transRef, {
                targetUserId,
                targetUserName: userSnap.data().name,
                adminName: adminName,
                pointsChange: -currentPoints,
                creditsChange: -currentCredits,
                reason: `RESET: ${reason}`,
                type: 'reset',
                status: 'approved',
                createdAt: serverTimestamp()
            });
        });
    },

    // 🟢 4. UTILITÁRIOS
    deleteTransactionWithReversal: async (transactionId) => {
        await runTransaction(db, async (transaction) => {
            const transRef = doc(db, paths.transactions, transactionId);
            const transSnap = await transaction.get(transRef);
            if (!transSnap.exists()) throw new Error("Transação não encontrada.");
            
            const data = transSnap.data();
            
            if (['approved', 'delivered'].includes(data.status) || data.deliveryStatus === 'delivered') {
                const targetRef = doc(db, paths.users, data.targetUserId);
                const targetSnap = await transaction.get(targetRef); // Corrigido nome da variável
                
                if (targetSnap.exists()) {
                    const currentPoints = targetSnap.data().points || 0;
                    const currentCredits = targetSnap.data().credits || 0;
                    
                    transaction.update(targetRef, {
                        points: currentPoints - (data.pointsChange || 0),
                        credits: currentCredits - (data.creditsChange || 0)
                    });
                }
            }
            transaction.delete(transRef);
        });
    },

    processReturn: async (transactionId, returnData, adminName) => {
        const { type, refundAmount, itemName, deductStock } = returnData;
        const transRef = doc(db, paths.transactions, transactionId);
        
        await runTransaction(db, async (transaction) => {
            const transSnap = await transaction.get(transRef);
            if (!transSnap.exists()) throw new Error("Transação não encontrada.");
            const originalData = transSnap.data();
            
            const userRef = doc(db, paths.users, originalData.targetUserId);
            const userSnap = await transaction.get(userRef);
            const returnLogRef = doc(collection(db, paths.transactions));
            
            if (type === 'refund') {
                if (userSnap.exists()) {
                    const currentCredits = userSnap.data().credits || 0;
                    transaction.update(userRef, { credits: currentCredits + Number(refundAmount) });
                }
                transaction.update(transRef, { deliveryStatus: 'returned', returnedAt: serverTimestamp() });
                transaction.set(returnLogRef, { 
                    targetUserId: originalData.targetUserId, targetUserName: originalData.targetUserName, adminName, 
                    type: 'return_refund', reason: `Devolução: ${itemName}`, creditsChange: Number(refundAmount), pointsChange: 0, status: 'approved', createdAt: serverTimestamp() 
                });
            } else if (type === 'exchange') {
                transaction.update(transRef, { deliveryStatus: 'exchanged', exchangedAt: serverTimestamp() });
                if (deductStock) {
                    const configRef = doc(db, paths.config, 'shopSettings');
                    const configSnap = await transaction.get(configRef);
                    if (configSnap.exists()) {
                        const stockData = configSnap.data();
                        const currentStock = stockData[itemName]?.stock || 0;
                        transaction.set(configRef, { [itemName]: { ...stockData[itemName], stock: Math.max(0, currentStock - 1) } }, { merge: true });
                    }
                }
                transaction.set(returnLogRef, {
                    targetUserId: originalData.targetUserId, targetUserName: originalData.targetUserName, adminName,
                    type: 'return_exchange', reason: `Troca por defeito: ${itemName}`, creditsChange: 0, pointsChange: 0, status: 'approved', createdAt: serverTimestamp()
                });
            }
        });
    },

    deleteTransaction: async (id) => deleteDoc(doc(db, paths.transactions, id)),
    
    updateTransaction: async (id, data) => {
        // Whitelist de campos permitidos para evitar escrita arbitrária
        const ALLOWED_FIELDS = [
            'status', 'deliveryStatus', 'deliveredAt', 'approvedAt', 'approvedBy',
            'returnedAt', 'exchangedAt', 'reason', 'targetUserName', 'notes',
            // Campos financeiros para edição de NF/resgate
            'cost', 'valueBRL', 'invoiceNumber', 'vendor'
        ];
        const safeData = Object.fromEntries(
            Object.entries(data).filter(([key]) => ALLOWED_FIELDS.includes(key))
        );
        if (Object.keys(safeData).length === 0) throw new Error("Nenhum campo válido para atualizar.");
        return updateDoc(doc(db, paths.transactions, id), safeData);
    },
    
    deliverItem: async (id) => updateDoc(doc(db, paths.transactions, id), { deliveryStatus: 'delivered', deliveredAt: serverTimestamp() }),
    
    toggleDelivery: async (id, status) => { 
        const n = status === 'delivered' ? 'pending' : 'delivered'; 
        await updateDoc(doc(db, paths.transactions, id), { deliveryStatus: n }); 
        return n;
    }
};